import { excerptFromIntranetBody, type IntranetFeedItem } from '@gb-ticket/shared-types';
import { MoreHorizontalIcon } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { API_BASE_URL } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useMe } from '@/platform/auth/use-me';
import { ColleagueNameButton } from '@/products/intranet/components/colleague-name-button';
import { IntranetBody } from '@/products/intranet/components/intranet-body';
import { IntranetEngagementPreview } from '@/products/intranet/components/intranet-engagement-bar';
import {
  CoverMedia,
  StreamCardFrame,
  StreamCardText,
} from '@/products/intranet/components/intranet-feed';
import {
  IntranetPollView,
  pollFromDraft,
  type IntranetPollDraft,
} from '@/products/intranet/components/intranet-poll';
import { useIntranetPoll } from '@/products/intranet/features/intranet/use-intranet-poll';

export type IntranetPreviewView = 'list' | 'grid' | 'detail';

export type IntranetLocalCover = { src: string; media: 'image' | 'video' };

const VIEWS: readonly { value: IntranetPreviewView; label: string }[] = [
  { value: 'list', label: 'Liste' },
  { value: 'grid', label: 'Raster' },
  { value: 'detail', label: 'Detail' },
];

/** Right column of the composer. Reads the form state and saves nothing. */
export function IntranetComposerPreview({
  item,
  body,
  localCover,
  pollDraft,
  savedPollSlug,
}: {
  /** Form state shaped like a feed item. `createdBy` falls back to the signed-in person. */
  item: IntranetFeedItem;
  body: string;
  /** Object URL of the cover picked before the first save. */
  localCover: IntranetLocalCover | null;
  /** Unsaved poll on a new entry. */
  pollDraft: IntranetPollDraft | null;
  /** Saved entries show the stored poll. */
  savedPollSlug?: string;
}) {
  const [view, setView] = useState<IntranetPreviewView>('list');
  const me = useMe();
  const excerpt = excerptFromIntranetBody(body);
  const shown: IntranetFeedItem = {
    ...item,
    teaser: excerpt.teaser,
    teaserHasMore: excerpt.hasMore,
    createdBy:
      item.createdBy.displayName || !me.data
        ? item.createdBy
        : {
            ...item.createdBy,
            id: me.data.id,
            displayName: me.data.displayName,
            profilePhotoUrl: me.data.profilePhotoUrl,
          },
    coverMedia: localCover?.media ?? item.coverMedia,
  };
  const src = localCover?.src ?? (item.coverUrl ? `${API_BASE_URL}${item.coverUrl}` : null);
  const menu = (
    <button
      type="button"
      disabled
      aria-label="Weitere Aktionen"
      className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground disabled:opacity-60"
    >
      <MoreHorizontalIcon className="size-4" aria-hidden />
    </button>
  );

  return (
    <div className="space-y-3">
      <div
        className="flex overflow-hidden rounded-lg border border-border bg-muted/30"
        role="group"
        aria-label="Vorschau-Ansicht"
      >
        {VIEWS.map((entry) => (
          <button
            key={entry.value}
            type="button"
            aria-pressed={view === entry.value}
            onClick={() => setView(entry.value)}
            className={cn(
              'min-h-11 flex-1 px-3 text-sm font-medium focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none',
              view === entry.value ? 'bg-brand-navy text-white' : 'text-foreground hover:bg-muted',
            )}
          >
            {entry.label}
          </button>
        ))}
      </div>

      {view === 'detail' ? (
        <article className="space-y-4 rounded-xl border bg-card p-4">
          {src ? <CoverMedia item={shown} src={src} /> : null}
          <header className="space-y-2">
            <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              {shown.publishedAt ? (
                formatDate(shown.publishedAt)
              ) : (
                <Badge variant="outline">Entwurf</Badge>
              )}
              {shown.category ? <Badge variant="secondary">{shown.category.name}</Badge> : null}
              <span>
                von{' '}
                <ColleagueNameButton
                  userId={shown.createdBy.id}
                  displayName={shown.createdBy.displayName}
                  className="font-medium text-foreground"
                />
              </span>
              <span className="ml-auto">{menu}</span>
            </p>
            <h2 className="text-2xl font-bold tracking-tight text-primary">{shown.title}</h2>
          </header>
          <IntranetBody body={body || null} />
          <ComposerPollPreview savedPollSlug={savedPollSlug} pollDraft={pollDraft} />
          <IntranetEngagementPreview item={shown} flush />
        </article>
      ) : (
        <StreamCardFrame
          item={shown}
          view={view}
          src={src}
          channel={shown.category?.name}
          menu={menu}
          footer={<IntranetEngagementPreview item={shown} />}
        >
          <div
            className={cn(
              'space-y-1 px-4 py-3',
              view === 'grid' && 'flex min-h-[7.5rem] flex-1 flex-col',
            )}
          >
            <h3 className={cn('font-semibold text-primary', view === 'grid' && 'line-clamp-2')}>
              {shown.title}
            </h3>
            <StreamCardText item={shown} view={view} onShowMore={() => setView('detail')} />
          </div>
          {view === 'list' ? (
            <div className="px-4 pb-3">
              <ComposerPollPreview savedPollSlug={savedPollSlug} pollDraft={pollDraft} />
            </div>
          ) : null}
        </StreamCardFrame>
      )}
    </div>
  );
}

function ComposerPollPreview({
  savedPollSlug,
  pollDraft,
}: {
  savedPollSlug?: string;
  pollDraft: IntranetPollDraft | null;
}) {
  const saved = useIntranetPoll(savedPollSlug);
  const poll = savedPollSlug
    ? (saved.data?.poll ?? null)
    : pollDraft
      ? pollFromDraft(pollDraft)
      : null;
  if (!poll) {
    return null;
  }
  return <IntranetPollView poll={poll} voting={false} previewMode />;
}
